import { JSONSchema7 } from 'json-schema';
import { ResourceId } from 'src/api/deinverband-api';
import { Id } from './common';

/**
 * Specify how a single property of a specific attribute should be rendered
 */
export const attributePropertyTypes = [
  // not editable (e.g. unknown)
  'const',
  // single editable value
  'atomic',
  // multiple atomic values
  'array',
  // custom enum type (values provided in tenant style)
  'enum',
  // data types (available on backend)
  'user',
  'company',
  'group',
  'role',
  'membership'
] as const;

export type AttributePropertyType = (typeof attributePropertyTypes)[number];

/**
 * JSON schema implemented in backend attributes
 */
export interface AttributeJsonSchema extends JSONSchema7 {
  properties?: {
    [key: string]: JSONSchema7 & {
      // x-refs are defined at root property level
      'x-refs'?: string[];
    };
  };
}

export type PropertyValue = Id | string | null;

/**
 * Single property of an attribute value
 */
export interface AttributeValueProperty {
  type: AttributePropertyType;
  path: string;
  value?: PropertyValue;
}

/**
 * Single property of an attribute matrix
 */
export interface AttributeMatrixProperty {
  type: AttributePropertyType;
  path: string;
  value?: PropertyValue[];
}

/**
 * Generic attribute type inference
 */
export interface AttributePropertyTypes {
  value: AttributeValueProperty;
  matrix: AttributeMatrixProperty;
}

export type AttributeFormat = keyof AttributePropertyTypes & string;

export type AttributeProperty<T extends AttributeFormat = AttributeFormat> =
  AttributePropertyTypes[T];

/**
 * Generic attribute value type
 */
export type GenericAttribute<T extends AttributeFormat = AttributeFormat> = {
  format: T;
  attributeType: string;
  properties: AttributeProperty<T>[];

  // the backend id or a negative id for creation (0 will be ignored)
  // the id is used during editing for attribute identification and
  // to discriminate between create and update operations on the backend
  id: number;
};

export type AttributeValue = GenericAttribute<'value'>;
export type AttributeMatrix = GenericAttribute<'matrix'>;

/**
 * Additional style for a specific attribute property
 */
export interface AttributePropertyStyle {
  label: string;
  overrides?: {
    type?: string;
    wildcard?: boolean;
  };
  // filters in the table hook format
  filters?: object;
  // static options for enums
  options?: {
    key: string;
    value: string;
    color?: string;
    icon?: string;
  }[];
  // default value for hidden fields
  // TODO: implement logic
  defaultValue?: PropertyValue | PropertyValue[];
}

/**
 * Additional styles per attribute property
 */
export interface AttributePropertyStyles {
  [path: string]: AttributePropertyStyle;
}

/**
 * Resolved attribute property value (for rendering purposes)
 */
export type ResolvedProperty<T extends AttributeFormat = AttributeFormat> = {
  attributeValue: GenericAttribute<T>;
  propertyStyle?: AttributePropertyStyle;
  propertyIndex?: number;
  propertyType?: string;
  property: AttributeProperty<T>;
};

export type ResolvedValueProperty = ResolvedProperty<'value'>;
export type ResolvedMatrixProperty = ResolvedProperty<'matrix'>;

/**
 * Define how to render an attribute component (in a specific case)
 */
export type AttributeComponentStyle =
  | AttributeMatrixStyle
  | AttributeCheckboxStyle
  | AttributeFormStyle
  | AttributeTextStyle
  | AttributeChipStyle
  | AttributeDoubleChipStyle
  | AttributeCardStyle;

export interface AttributeMatrixStyle {
  type: 'Matrix';
  properties: string | string[];
}

export interface AttributeCheckboxStyle {
  type: 'Checkbox';
  property: string;
}

export interface AttributeFormStyle {
  type: 'Form';
  properties: string | string[];
}

export interface AttributeTextStyle {
  type: 'Text';
  properties: string | string[];
}

export interface AttributeChipStyle {
  type: 'Chip';
  properties: string | string[];
  colorize?: boolean;
  icon?: string;
}

export interface AttributeDoubleChipStyle {
  type: 'DoubleChip';
  primary: string | string[];
  secondary: string | string[];
  colorize?: 'primary' | 'secondary';
}

export interface AttributeCardStyle {
  type: 'Card';
  properties: string[];
}

export interface AttributeExtra {
  location: string;
  survey: string;
  event: string;
}

/**
 * Configuration for a single attribute type provided by the tenant style
 */
export interface AttributeStyle {
  name: string;
  viewer?: AttributeComponentStyle;
  editor?: AttributeComponentStyle;
  properties?: AttributePropertyStyles;
  // advanced property handling
  extra?: AttributeExtra;
}

/**
 * The attributes configuration provided by the tenant style
 */
export type AttributesStyle = Record<string, AttributeStyle>;

/**
 * Interface for entities with attributes
 */
export interface Attributed {
  attributes: AttributeValue[];
}

export interface AttributesPatch {
  update?: AttributeValue[];
  remove?: number[];
}

export type AttributedPatch<T extends Attributed = Attributed> = Omit<
  T,
  'attributes'
> & {
  attributes: AttributesPatch;
};

export type AttributeCondition = {
  type: 'Condition';
  name: string;
  value: unknown;
};

export type AttributeGroup = {
  type: 'Group';
  operation: 'AND' | 'OR';
  filters: AttributeFilter[];
};

export type AttributeNot = {
  type: 'Not';
};

export type AttributeFilter = AttributeCondition | AttributeGroup | AttributeNot;

export type SurveyParticipantStatus =
  | 'NO_ANSWER'
  | 'PARTIALLY_ANSWERED'
  | 'PUBLISHED';

/**
 * Declares the property types for a static attribute
 */
export type StaticAttributeSchema = Record<string, AttributePropertyType>;

/**
 * Derives a value type from a static attribute schema
 */
export type StaticAttributeValue<T extends StaticAttributeSchema> = {
  [K in keyof T]: T[K] extends 'array'
    ? (ResourceId | string | null)[]
    : ResourceId | string | null;
};

export const staticAttributeSchemas = {
  Admin: {},
  Editor: { editorId: 'user' },
  Viewer: { viewerId: 'user' },
  Creator: { creatorId: 'user' },
  CustomAccess: { userId: 'user' },
  CustomDeny: { userId: 'user' },
  AttendeeStatus: { eventId: 'atomic', statuses: 'array' },
  SurveyParticipantStatus: { surveyId: 'atomic', statuses: 'array' }
} as const;

export type StaticAttributeName = keyof typeof staticAttributeSchemas;

/**
 * Typed value for a specific static attribute name
 */
export type StaticAttributeValueByName<K extends StaticAttributeName> =
  StaticAttributeValue<(typeof staticAttributeSchemas)[K]>;

